import { RouterChangeEventParam, RouterPushParams } from "./types";
import { Route } from "./route";

export class RouteManager {
  private routesMap: Map<string, Route>;

  private listeners: Array<(params: RouterChangeEventParam) => void> = [];

  public pathVariable: Map<string, string>;
  public query: Map<string, string>;
  public pathname: string;
  public href: string;
  public search: string;

  private pagePayload?: any;

  private historyStack: string[] = [];
  private historyIndex: number = -1;

  constructor() {
    this.routesMap = new Map<string, Route>();
    this.pathname = window.location.pathname;
    this.href = window.location.href;
    this.search = window.location.search;
    this.pathVariable = new Map<string, string>();
    this.query = this.getQueryParams(new URL(window.location.href));
    this.historyStack = [window.location.pathname];
    this.historyIndex = 0;
    //
    this.setup();
  }

  private normalizePath(path: string) {
    if (path.length > 1) {
      return path.replace(/\/+$/, "");
    }
    return path;
  }

  private matchRoutePath(pathname: string, routePath: string) {
    const normalizedPath = this.normalizePath(pathname);
    const normalizedRoute = this.normalizePath(routePath);

    const pathSegments = normalizedPath.split("/").filter(Boolean);
    const routeSegments = normalizedRoute.split("/").filter(Boolean);

    if (pathSegments.length !== routeSegments.length) return null;

    const params = new Map<string, string>();

    for (let i = 0; i < routeSegments.length; i++) {
      const routeSegment = routeSegments[i];
      const pathSegment = pathSegments[i];

      if (routeSegment.startsWith(":")) {
        const paramName = routeSegment.slice(1);
        params.set(paramName, decodeURIComponent(pathSegment));
        continue;
      }

      if (routeSegment !== pathSegment) return null;
    }

    return params;
  }

  private setup() {
    window.onpopstate = () => {
      const url = new URL(window.location.href);
      const newPath = url.pathname;

      const prevIndex = this.historyIndex - 1;
      const nextIndex = this.historyIndex + 1;

      if (prevIndex >= 0 && this.historyStack[prevIndex] === newPath) {
        this.historyIndex = prevIndex;
      } else if (
        nextIndex < this.historyStack.length &&
        this.historyStack[nextIndex] === newPath
      ) {
        this.historyIndex = nextIndex;
      }

      this.updateRouteData(url);
      this.pagePayload = undefined;
      this.listeners.forEach((listener) =>
        listener({ href: url.pathname, payload: undefined })
      );
    };
  }

  public push(hrefData: RouterPushParams) {
    const payload = typeof hrefData === "object" ? hrefData.payload : undefined;
    const href = typeof hrefData === "object" ? hrefData.pathname : hrefData;

    this.updateRouteData(new URL(this.getFullURL(href)));
    this.pagePayload = payload;

    this.listeners.forEach((listener) => listener({ href, payload }));
    this.historyStack = this.historyStack.slice(0, this.historyIndex + 1);
    this.historyStack.push(href);
    this.historyIndex++;
    window.history.pushState(null, "", href);
  }

  public back(defaultPath?: string) {
    if (this.historyIndex > 0) {
      window.history.back();
    } else if (defaultPath !== undefined) {
      this.push(defaultPath);
    }
  }

  public forward(defaultPath?: string) {
    if (this.historyIndex < this.historyStack.length - 1) {
      window.history.forward();
    } else if (defaultPath !== undefined) {
      this.push(defaultPath);
    }
  }

  private updateRouteData(url: URL) {
    this.href = url.href;
    this.search = url.search;
    this.pathname = url.pathname;
    this.query = this.getQueryParams(url);
  }

  private getQueryParams(url: URL) {
    return new Map<string, string>(url.searchParams.entries());
  }

  public getPagePayload() {
    return this.pagePayload;
  }

  public addRoute(route: Route) {
    this.routesMap.set(route.getPath(), route);
  }

  public getRoute(path: string) {
    return this.routesMap.get(path);
  }

  public getCurrentRoute() {
    const currentPath = this.normalizePath(this.pathname);

    for (const route of this.routesMap.values()) {
      const params = this.matchRoutePath(currentPath, route.getPath());
      if (params) {
        this.pathVariable = params;
        return route;
      }
    }

    this.pathVariable = new Map<string, string>();
    return undefined;
  }

  public getComponentByPath(path: string) {
    const route = this.routesMap.get(path);
    return route ? route.getComponent() : undefined;
  }

  public getRoutesMap() {
    return this.routesMap;
  }

  public getRoutesArray() {
    return Array.from(this.routesMap.values());
  }

  public getFullURL(href: string) {
    const origin = window.location.origin;
    const fullUrl = href.indexOf("/") === 0 ? `${origin}${href}` : href;
    return fullUrl;
  }

  public onRouteChange(handler: (params: RouterChangeEventParam) => void) {
    if (this.listeners.find((listener) => listener === handler)) return;
    this.listeners.push(handler);
  }

  public removeOnRouteChange(
    handler: (params: RouterChangeEventParam) => void
  ) {
    this.listeners = this.listeners.filter((listener) => listener !== handler);
  }
}
